import React, {
    useEffect,
    useState,
    useContext,
    useCallback
} from 'react';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useModal } from '@jutro/components';
import { Wizard } from 'e1p-portals-wizard-react';
import { LoadSaveService } from 'e1p-capability-quoteandbind';
import { E1PLoader } from 'e1p-capability-policyjob-react';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { QuoteInfoComponentContext } from 'gw-capability-quoteandbind-common-react';
import { MockUpUtil } from '@xengage/gw-portals-util-js';
import { commonMessages as e1pCommonMessages } from 'e1p-platform-translations';
import messages from './EAWizard.messages';
import wizardStepToFieldMapping from './config/ea-wizard-step-to-field-mapping.json5';
import EaQuoteInfo from './components/EaQuoteInfo/EaQuoteInfo';
import wizardConfig from './config/ea-wizard-config.json5';

const PATH_TO_MOCK_DATA = 'quote.pa';
const MOCK_DATA_TO_SET = [
    'baseData.accountHolder.person.emailAddress1',
    'baseData.accountHolder.person.accountHolder',
    'baseData.policyAddress.addressLine1',
    'baseData.policyAddress.city',
    'baseData.productCode',
    'baseData.accountHolder.person.firstName',
    'baseData.accountHolder.person.lastName',
    'baseData.accountHolder.person.subtype',
    'baseData.accountHolder.person.dateOfBirth.day',
    'baseData.accountHolder.person.dateOfBirth.month',
    'baseData.accountHolder.person.dateOfBirth.year'
];
const MOCK_DATA_TO_REMOVE = [
    'baseData.accountHolder.person.emailAddress1',
    'baseData.accountHolder.person.firstName',
    'baseData.accountHolder.person.lastName',
    'baseData.accountHolder.person.dateOfBirth',
    'baseData.policyAddress.addressLine1',
    'baseData.policyAddress.city'
];

const ACTION_MSG = {
    cancelAction: {
        title: messages.exitTransaction,
        message: messages.infoWillBeSavedAsDraft,
        messageProps: {
            confirmButtonText: messages.exitLabel,
            cancelButtonText: messages.cancelLabel
        }
    }
};

function setCoverageStartDate() {
    const currentDate = new Date();
    const coverageStartDate = {
        day: currentDate.getDate(),
        month: currentDate.getMonth(),
        year: currentDate.getFullYear()
    };
    return coverageStartDate;
}

function setPrepData(address) {
    const realData = {
        baseData: {
            policyAddress: address,
            periodStartDate: setCoverageStartDate()
        }
    };

    return MockUpUtil.setMockData(
        realData,
        PATH_TO_MOCK_DATA,
        ...MOCK_DATA_TO_SET
    );
}

function removeMockData(submission) {
    return MockUpUtil.cleanUpMockedProperties(
        submission,
        PATH_TO_MOCK_DATA,
        ...MOCK_DATA_TO_REMOVE
    );
}

function createSubmissionOnPartialAddress(address) {
    const data = setPrepData(address);
    return LoadSaveService.createSubmission(data);
}

function EAWizard(props) {
    const modalApi = useModal();
    const { steps, title } = wizardConfig;
    const [initialSubmission, setInitialSubmission] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [shouldSkipValidSteps, setShouldSkipValidSteps] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);
    const { location, history } = props;

    useEffect(() => {
        const viewModelContext = {
            AccountEmailRequired: true,
            DriverEmailRequired: true,
            AccountDOBRequired: true
        };

        if (!viewModelService) {
            history.push('/');
            return;
        }

        let submissionPromise;
        if (_.has(location, 'state.address')) {
            const { address } = location.state;
            submissionPromise = createSubmissionOnPartialAddress(address);
        } else if (_.has(location, 'state.submission')) {
            const { submission } = location.state;
            setShouldSkipValidSteps(true);
            submissionPromise = Promise.resolve(submission);
        } else if (_.has(location, 'search')) {
            setShouldSkipValidSteps(true);
            const parsedParms = queryString.parse(location.search);
            submissionPromise = LoadSaveService.retrieveSubmission(parsedParms);
        } else {
            return;
        }

        submissionPromise.then((response) => {
            const submissionVM = viewModelService.create(
                removeMockData(response),
                'pc',
                'edge.capabilities.quote.submission.dto.QuoteDataDTO',
                viewModelContext
            );

            setInitialSubmission(submissionVM);
            setIsLoading(false);
        });
        // only execute this once per component lifecycle
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCancel = useCallback((wizardProps) => {
        const { wizardSnapshot, wizardData } = wizardProps;
        const latestSnapshot = (wizardData.aspects.subtreeValid && wizardData.aspects.valid)
            ? wizardData : wizardSnapshot;

        const periodStatus = _.get(wizardSnapshot, 'baseData.periodStatus.value.code');
        const quoteID = _.get(wizardSnapshot, 'quoteID');
        if (periodStatus === 'Bound') {
            // allow transition once the submission is bound
            return true;
        }
        return modalApi.showConfirm(ACTION_MSG.cancelAction).then((results) => {
            if (results === 'cancel') {
                return _.noop();
            }
            let serverCall = LoadSaveService.updateDraftSubmission;

            if (periodStatus === 'Quoted') {
                serverCall = LoadSaveService.updateQuotedSubmission;
            }

            const wizardDataFromSnapshot = _.get(latestSnapshot, 'value');
            const dataToSave = periodStatus === 'Quoted' ? MockUpUtil.setMockData(
                wizardDataFromSnapshot,
                PATH_TO_MOCK_DATA
            ) : MockUpUtil.setMockData(
                wizardDataFromSnapshot,
                PATH_TO_MOCK_DATA,
                ...MOCK_DATA_TO_SET
            );
            setIsLoading(true);

            const redirectPath = periodStatus ? `/quotes/${quoteID}/summary` : '/';

            serverCall(dataToSave).then(() => {
                history.push(redirectPath);
            }).catch(() => {
                modalApi.showAlert({
                    title: messages.exitTransaction,
                    message: messages.infoWillBeSavedAsDraft,
                    messageProps: {
                        confirmButtonText: e1pCommonMessages.exitTransactionLabel,
                        cancelButtonText: messages.cancelLabel
                    }
                });
            }).finally(() => {
                setIsLoading(false);
            });
        });
    }, [history]);

    if (isLoading) {
        return (
            <E1PLoader loaded={!isLoading} />
        );
    }

    if (!initialSubmission) {
        return null;
    }

    return (
        <QuoteInfoComponentContext.Provider value={EaQuoteInfo}>
            <Wizard
                initialSteps={steps}
                wizardTitle={title}
                initialData={initialSubmission}
                onCancel={handleCancel}
                skipCompletedSteps={shouldSkipValidSteps}
                wizardStepToFieldMapping={wizardStepToFieldMapping}
            />
        </QuoteInfoComponentContext.Provider>
    );
}

EAWizard.propTypes = {
    location: PropTypes.shape({
        search: PropTypes.string,
        state: PropTypes.shape({
            address: PropTypes.shape({}),
            submission: PropTypes.shape({})
        }),
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
};

export default EAWizard;
